// ─── SHARED ONBOARDING STYLES ─────────────────────────────────────────────────
// Small style helpers shared between OnboardingFlow.jsx (Section 1 + the
// transition screen) and OnboardingSection2.jsx, matching the app's pastel
// aesthetic (see src/theme/sharedStyles.js for the equivalent used elsewhere).
// Layout is left-aligned/top-down (not centered on the screen) — a back icon
// and a progress bar sit in a header row, content flows underneath, and an
// optional full-width action button is pinned to the bottom.
import { ONBOARDING_VARS } from "../theme/onboardingPalette";

export const screenWrap = {
  ...ONBOARDING_VARS, // fixed blue-violet palette, see theme/onboardingPalette.js
  position: "fixed",
  inset: 0,
  // Several floating action buttons across the app (HabitsTab, CalendarTab,
  // CategoriesTab) are portalled straight to document.body at zIndex 99999,
  // higher than this overlay used to sit at (30000) — they were rendering
  // (and staying tappable) right on top of the onboarding quiz. This needs
  // to clear all of those; CoachMarkTour intentionally stays lower (30001/
  // 30002) since its spotlight is a see-through cutout, not a full takeover.
  zIndex: 999999,
  background: "var(--c-hero-gradient, linear-gradient(160deg,#fff5f9,#fdeef4))",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'Quicksand', sans-serif",
  overflow: "hidden",
};

// Caps and centers the header/content/footer column inside screenWrap's
// full-bleed background — screenWrap has no max-width of its own (the
// gradient behind it is meant to fill the viewport), so on a wide desktop
// window every optionBtn (width: 100%) stretched edge-to-edge with it
// unless something narrower wraps them. 560px keeps the two-line question
// headings and option cards reading like a normal question card instead of
// a form spanning the whole monitor.
export const screenColumn = {
  width: "100%",
  maxWidth: 560,
  margin: "0 auto",
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
};

// paddingTop leads with var(--sat, 0px) (the notch/status-bar safe-area
// inset, set globally in public/index.html) — screenWrap is portalled
// straight to document.body and positioned fixed/inset:0, so without this
// the back button and dots row render right under the status bar icons on
// native instead of below them.
export const headerRow = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "calc(var(--sat, 0px) + 20px) 20px 0",
  flexShrink: 0,
};

export const backIconBtn = {
  width: 38,
  height: 38,
  borderRadius: "50%",
  border: "none",
  background: "var(--c-bg-card, #fff)",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  color: "var(--c-pink, var(--c-text-accent, #333))",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  padding: 0,
};

export const backIconSpacer = { width: 38, height: 38, flexShrink: 0 };

// Text link shown top-right of the header row, next to the progress bar —
// used only on the job-title/course screens, where the answer is optional
// and typing it out or hunting through the list isn't always worth it.
export const skipTextBtn = {
  border: "none",
  background: "none",
  color: "var(--c-text-soft, #999)",
  fontFamily: "inherit",
  fontSize: 13.5,
  fontWeight: 700,
  cursor: "pointer",
  padding: "6px 2px",
  flexShrink: 0,
};

// Page-indicator dots, replacing the old single continuous progress bar.
// One dot per page in the current section (progress/total, same numbers the
// bar used) — done/current pages are filled with the accent gradient,
// upcoming ones stay a plain gray circle. Centered in the header row between
// the back button and (on the optional screens) the Skip link.
export const dotsRow = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};

export const dot = (state) => ({ // state: "done" | "current" | "upcoming"
  width: state === "current" ? 10 : 7,
  height: state === "current" ? 10 : 7,
  borderRadius: "50%",
  flexShrink: 0,
  background: state === "upcoming"
    ? "var(--c-border-mid, #eee)"
    : state === "current"
      ? "var(--c-pink, #d4789a)"
      : "var(--c-accent-strong, var(--c-primary-deep, #d4789a))",
  transition: "background 0.3s ease, width 0.25s ease, height 0.25s ease",
});

// The scrollable body under the header — left-aligned, not centered.
// scrollbarWidth/gutter: the app's global ::-webkit-scrollbar is 4px wide and
// takes real layout space, so a scrollbar appearing once overflow is detected
// (a frame after first paint) used to shrink this box by 4px mid-render. That
// was enough to re-wrap a heading sitting right at its line-break threshold
// (e.g. "{name}, how's life feeling lately?"), which read as the text visibly
// shifting a split second after the screen appeared. Keeping the scrollbar out
// of layout entirely makes the width constant, so text wraps once and stays put.
export const contentArea = {
  flex: 1,
  overflowY: "auto",
  scrollbarWidth: "none",
  WebkitOverflowScrolling: "touch",
  display: "flex",
  flexDirection: "column",
  gap: 18,
  padding: "26px 22px 24px",
  boxSizing: "border-box",
};

// Non-scrolling variant of contentArea, for screens that manage their own
// internal scroll region (the search+A-Z course/job pickers) — the heading
// and search bar need to stay put while only the list beneath scrolls.
export const contentAreaFixed = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  gap: 14,
  padding: "26px 22px 24px",
  boxSizing: "border-box",
};

// Sticky full-width action bar pinned to the bottom, used by every screen
// that keeps an explicit continue/Next button (multi-select questions, the
// free-text name field, and any screen with a secondary action alongside
// its main choice).
export const bottomBar = {
  flexShrink: 0,
  padding: "10px 20px calc(16px + env(safe-area-inset-bottom, 0px))",
  display: "flex",
  gap: 10,
};

export const optionBtn = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: 14,
  width: "100%",
  textAlign: "left",
  padding: "20px 22px",
  borderRadius: 20,
  border: `2px solid ${active ? "var(--c-accent-strong, var(--c-primary-deep, #d4789a))" : "var(--c-border-mid, #eee)"}`,
  background: active ? "var(--c-bg-active, #fdeef4)" : "var(--c-bg-card, #fff)",
  color: "var(--c-text, #333)",
  fontFamily: "inherit",
  fontSize: 16.5,
  fontWeight: 700,
  lineHeight: 1.35,
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(70,80,160,0.08)",
});

export const primaryBtn = (disabled) => ({
  border: "none",
  borderRadius: 18,
  padding: "16px 22px",
  background: disabled
    ? "var(--c-border-mid, #eee)"
    : "linear-gradient(135deg, var(--c-primary-light, #f0a8c8), var(--c-primary-deep, #d4789a))",
  color: disabled ? "var(--c-text-soft, #aaa)" : "var(--c-on-primary, #fff)",
  fontFamily: "inherit",
  fontSize: 16,
  fontWeight: 700,
  cursor: disabled ? "default" : "pointer",
  width: "100%",
});

export const textBtn = {
  background: "none",
  border: "none",
  color: "var(--c-text-soft, #999)",
  fontFamily: "inherit",
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
  padding: 10,
};

export const textInput = {
  width: "100%",
  border: "2px solid var(--c-border-mid, #eee)",
  borderRadius: 18,
  padding: "17px 18px",
  fontFamily: "inherit",
  fontSize: 17,
  color: "var(--c-text, #333)",
  background: "var(--c-bg-card, #fff)",
  boxSizing: "border-box",
};

export const heading = {
  fontSize: 25,
  fontWeight: 700,
  color: "var(--c-text-accent, #333)",
  marginBottom: 6,
  lineHeight: 1.3,
  textAlign: "center",
};

export const subline = {
  fontSize: 14.5,
  color: "var(--c-text, #000)",
  lineHeight: 1.4,
  textAlign: "center",
};

// Full-width row chip (icon left, label next to it, trailing +/check) —
// used by the starter-categories and starter-habits screens. Sized to match
// the Finch reference: tall, generous padding, icon+label vertically
// centered within the row.
export function chip({ active }) {
  return {
    display: "flex",
    alignItems: "center",
    gap: 14,
    width: "100%",
    padding: "18px 20px",
    borderRadius: 20,
    border: `2px solid ${active ? "var(--c-accent-strong, var(--c-primary-deep, #d4789a))" : "var(--c-border-mid, #eee)"}`,
    background: active ? "var(--c-bg-active, #fdeef4)" : "var(--c-bg-card, #fff)",
    color: "var(--c-text, #333)",
    fontFamily: "inherit",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(70,80,160,0.08)",
  };
}
